import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { NAV, AUTH } from "@/constants/testIds";
import { HeartHandshake, LogOut, Menu, ShieldCheck, Phone, Mail, UserCog } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

export const CONTACT_PHONE = "888.448.0430";
export const CONTACT_PHONE_HREF = "tel:+18884480430";
export const CONTACT_EMAIL = "contactsupport@autismbabysitters.com";
export const CONTACT_EMAIL_HREF = "mailto:contactsupport@autismbabysitters.com";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (p) => location.pathname === p || location.pathname.startsWith(p + "/");

  const linkCls = (active) =>
    `text-sm font-medium transition-colors duration-200 ${
      active ? "text-sage-900" : "text-stone2-600 hover:text-sage-700"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone2-200">
      {/* Contact strip — always visible */}
      <div className="bg-sage-900 text-stone2-50 text-xs" data-testid="nav-contact-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-9 flex items-center justify-between gap-3">
          <span className="hidden sm:inline text-stone2-200/80">Need help finding a specialized sitter? Talk to a real person.</span>
          <span className="sm:hidden text-stone2-200/80 font-medium">We can help you find someone.</span>
          <div className="flex items-center gap-4">
            <a
              href={CONTACT_PHONE_HREF}
              data-testid="nav-contact-phone"
              className="flex items-center gap-1.5 font-semibold text-ochre-300 hover:text-ochre-200 transition-colors duration-200"
            >
              <Phone className="w-3.5 h-3.5" />
              <span>{CONTACT_PHONE}</span>
            </a>
            <a
              href={CONTACT_EMAIL_HREF}
              data-testid="nav-contact-email"
              className="hidden sm:flex items-center gap-1.5 font-semibold text-stone2-100 hover:text-white transition-colors duration-200"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>{CONTACT_EMAIL}</span>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid={NAV.brand} className="flex items-center gap-2 group">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-sage-600 text-white group-hover:bg-sage-700 transition-colors duration-200">
            <HeartHandshake className="w-5 h-5" />
          </span>
          <span className="font-heading font-bold tracking-tight text-lg text-sage-900">
            Autism<span className="text-ochre-600">Babysitters</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {user && user.role === "family" && (
            <>
              <Link to="/family" data-testid={NAV.dashboard} className={linkCls(isActive("/family"))}>
                Dashboard
              </Link>
              <Link to="/search" data-testid={NAV.search} className={linkCls(isActive("/search"))}>
                Find sitters
              </Link>
              <Link to="/bookings" data-testid={NAV.bookings} className={linkCls(isActive("/bookings"))}>
                Bookings
              </Link>
              <Link to="/messages" data-testid={NAV.messages} className={linkCls(isActive("/messages"))}>
                Messages
              </Link>
              <Link to="/favorites" data-testid="nav-favorites-link" className={linkCls(isActive("/favorites"))}>
                Favorites
              </Link>
              <Link to="/insights" data-testid="nav-insights-link" className={linkCls(isActive("/insights"))}>
                Insights
              </Link>
              <Link to="/incidents" data-testid="nav-incidents-link" className={linkCls(isActive("/incidents"))}>
                Incidents
              </Link>
            </>
          )}
          {user && user.role === "sitter" && (
            <>
              <Link to="/sitter" data-testid={NAV.dashboard} className={linkCls(isActive("/sitter"))}>
                Dashboard
              </Link>
              <Link to="/bookings" data-testid={NAV.bookings} className={linkCls(isActive("/bookings"))}>
                Bookings
              </Link>
              <Link to="/messages" data-testid={NAV.messages} className={linkCls(isActive("/messages"))}>
                Messages
              </Link>
              <Link to="/incidents" data-testid="nav-incidents-link" className={linkCls(isActive("/incidents"))}>
                Incidents
              </Link>
            </>
          )}
          {user && user.role === "admin" && (
            <>
              <Link to="/admin" data-testid={NAV.dashboard} className={linkCls(isActive("/admin"))}>
                Admin
              </Link>
              <Link to="/admin/promo-codes" data-testid="nav-admin-promo" className={linkCls(isActive("/admin/promo-codes"))}>
                Promos
              </Link>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/blog" data-testid="nav-blog-link"
            className="hidden sm:inline text-sm font-semibold text-sage-800 hover:text-sage-900 transition-colors duration-200">
            Blog
          </Link>
          {!user && (
            <>
              <Link to="/pricing" data-testid="nav-pricing-link" className="text-sm font-semibold text-sage-800 hover:text-sage-900 transition-colors duration-200">
                Pricing
              </Link>
              <Link to="/login" data-testid={NAV.loginBtn} className="text-sm font-semibold text-sage-800 hover:text-sage-900 transition-colors duration-200">
                Log in
              </Link>
              <Link to="/register" data-testid={NAV.registerBtn} className="btn-primary text-sm">
                Get started
              </Link>
            </>
          )}
          {user && (
            <>
              <NotificationBell />
              <Link to="/settings/subscription" data-testid="nav-settings-subscription" title="Subscription"
                className="hidden sm:inline text-xs font-semibold text-sage-700 hover:text-sage-900">
                Membership
              </Link>
              <Link to="/settings/account" data-testid="nav-settings-account" title="Account settings"
                className="p-2 rounded-full text-stone2-600 hover:bg-stone2-100 hidden sm:inline-flex">
                <UserCog className="w-4 h-4" />
              </Link>
              <Link to="/settings/2fa" data-testid="nav-settings-2fa" title="Two-factor auth"
                className="p-2 rounded-full text-stone2-600 hover:bg-stone2-100 hidden sm:inline-flex">
                <ShieldCheck className="w-4 h-4" />
              </Link>
              <span className="hidden sm:inline text-sm text-stone2-600" data-testid="nav-user-name">
                Hi, {user.name?.split(" ")[0] || user.email}
              </span>
              <button
                type="button"
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid={AUTH.logoutBtn}
                className="btn-secondary text-sm"
              >
                <LogOut className="w-4 h-4" /> Log out
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
